// Pure tab-selection rules for the phone Info sheet. On a phone the sheet is a manual toggle, so it
// must land on the entity the player just selected instead of keeping whatever tab was open
// before. Desktop never passes `follow`, so its behaviour is the historical "stay on the last tab".

/** A deliberate sub-tab click, remembered against the selection it was made on. */
export interface SubTabPick {
  readonly tab: string;
  readonly forSelectionId: string;
}

/** The sub-tab that shows the selected node itself (falls back to the first available). */
export function matchingSubTab(selectedType: string, available: readonly string[]): string {
  if (available.includes(selectedType)) return selectedType;
  return available[0] ?? 'tile';
}

/**
 * Which SelectionTab sub-tab to show.
 * - `follow` off (desktop): the last chosen tab if still available, else the first one.
 * - `follow` on (phone): a pick made on THIS selection wins; otherwise the tab matching the
 *   selected node, so a new selection always resets to its own entity.
 */
export function resolveSubTab(args: {
  readonly available: readonly string[];
  readonly selectedType: string;
  readonly selectionId: string;
  readonly follow: boolean;
  /** Desktop state: last chosen tab (initialised to the first available). */
  readonly current: string;
  readonly pick: SubTabPick | null;
}): string {
  const { available, selectedType, selectionId, follow, current, pick } = args;
  if (!follow) return available.includes(current) ? current : (available[0] ?? 'tile');
  if (pick !== null && pick.forSelectionId === selectionId && available.includes(pick.tab)) {
    return pick.tab;
  }
  return matchingSubTab(selectedType, available);
}

/**
 * Should the shell jump from Overview/Diplomacy to the Selection tab? Only when following, the
 * selection identity changed to a non-null one, and the Selection tab exists. A tab the user
 * picks afterwards is left alone until the selection changes again.
 */
export function shouldJumpToSelectionTab(args: {
  readonly follow: boolean;
  readonly previousKey: string | null;
  readonly key: string | null;
  readonly hasSelectionTab: boolean;
}): boolean {
  const { follow, previousKey, key, hasSelectionTab } = args;
  return follow && key !== null && key !== previousKey && hasSelectionTab;
}
